// src/components/AppLayout.js
// Updated: 11/09/2025 09:36 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '../context/AuthContext';
import { auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { AlertTriangle, LogOut } from 'lucide-react';

const logStopImpersonation = httpsCallable(functions, 'logStopImpersonation');

/**
 * Banner for admin impersonation
 */
function ImpersonationBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  // --- 1. REMOVED unused [adminOrgId, setAdminOrgId] state ---

  useEffect(() => {
    const adminId = sessionStorage.getItem('impersonating_admin_id');
    if (adminId) {
      setIsImpersonating(true);
      // --- 2. REMOVED logic that set the unused state ---
    }
    // --- 3. Changed dependency array to empty ---
  }, []);

  const handleStopImpersonating = async () => {
    try {
      const revertToken = sessionStorage.getItem('impersonation_revert_token');
      const adminOrgIdToLog = sessionStorage.getItem('impersonating_admin_org_id');

      // 1. Log out the current (impersonated) user
      await logout();

      // 2. Clear all session flags
      sessionStorage.removeItem('impersonating_admin_id');
      sessionStorage.removeItem('impersonation_revert_token');
      sessionStorage.removeItem('impersonating_admin_org_id');

      if (revertToken) {
        // 3. Use the revert token to sign the admin back in
        await signInWithCustomToken(auth, revertToken);
        
        // 4. NOW call the log function, as the admin
        if (adminOrgIdToLog) {
          try {
            await logStopImpersonation({ originalOrgId: adminOrgIdToLog });
          } catch (logErr) {
            console.error("Failed to log impersonation stop:", logErr);
            // Don't block navigation for a log failure
          }
        }
        
        // 5. Navigate to the dashboard (now as the admin)
        navigate('/dashboard');
      } else {
        // 6. Fallback if the token is missing
        navigate('/login');
      }
    } catch (err) {
      console.error('Failed to stop impersonating:', err);
      sessionStorage.removeItem('impersonating_admin_id');
      sessionStorage.removeItem('impersonation_revert_token');
      sessionStorage.removeItem('impersonating_admin_org_id');
      navigate('/login');
      alert('Failed to stop impersonating. You have been logged out.');
    }
  };

  if (!isImpersonating) {
    return null; // Don't show anything if not impersonating
  }

  return (
    <div className="z-50 flex w-full items-center justify-center bg-yellow-500 p-3 text-black shadow-lg print-hide">
      <AlertTriangle className="mr-3 h-6 w-6" />
      <span className="mr-4 text-sm font-semibold">
        You are currently impersonating a user.
      </span>
      <button
        onClick={handleStopImpersonating}
        className="flex items-center rounded-md border border-black bg-white px-3 py-1 text-sm font-medium text-black transition hover:bg-gray-100"
      >
        <LogOut className="mr-2 h-4 w-4" />
        Stop Impersonating
      </button>
    </div>
  );
}


/**
 * Main layout component
 */
export default function AppLayout({ children }) {
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <ImpersonationBanner />
        <Header />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6">
          <div className="mx-auto max-w-7xl">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}